if (!customElements.get('free-gift-controller')) {
  class FreeGiftController extends HTMLElement {
    constructor () {
      super();

      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
      this.modalSelector = this.dataset.freeGiftModalSelector || 'advanced-modal[data-free-gift-modal]';
      this.fallbackGiftVariantId = this.normalizeId(this.dataset.freeGiftVariantId);
      this.modalOpenDelay = this.normalizeDelay(this.dataset.modalOpenDelay, 180);
      this.requestTokenField = '_fgc_request_token';

      this.form = null;
      this.modal = null;
      this.modalDialog = null;
      this.modalForm = null;
      this.hasModalFlow = false;
      this.isEnabled = false;
      this.activeModalSubmitter = null;
      this.modalOpenTimer = null;

      this.handleModalSubmit = this.handleModalSubmit.bind(this);
      this.handleModalClose = this.handleModalClose.bind(this);
      this.handleOwnFormSubmit = this.handleOwnFormSubmit.bind(this);
    }

    connectedCallback () {
      this.form = this.querySelector('form[action*="/cart/add"]');
      if (!this.form) return;

      this.modal = document.querySelector(this.modalSelector);
      this.modalDialog = this.modal?.querySelector('dialog') ?? null;
      this.modalForm = this.modal?.querySelector('form[action*="/cart/add"]') ?? null;
      this.hasModalFlow = Boolean(this.modal && this.modalDialog && this.modalForm);
      this.isEnabled = true;
      this.form.addEventListener('submit', this.handleOwnFormSubmit, true);

      if (this.hasModalFlow) {
        this.modalForm.addEventListener('submit', this.handleModalSubmit, true);
        this.modalDialog.addEventListener('theme:popup:onclose', this.handleModalClose);
      }

      FreeGiftController.controllers.add(this);
      FreeGiftController.ensureXhrInterceptor();
    }

    disconnectedCallback () {
      if (this.form) {
        this.form.removeEventListener('submit', this.handleOwnFormSubmit, true);
      }

      if (this.modalForm) {
        this.modalForm.removeEventListener('submit', this.handleModalSubmit, true);
      }

      if (this.modalDialog) {
        this.modalDialog.removeEventListener('theme:popup:onclose', this.handleModalClose);
      }

      FreeGiftController.controllers.delete(this);

      if (FreeGiftController.pendingRequest && FreeGiftController.pendingRequest.controller === this) {
        this.stopModalLoadingIndicators();
        FreeGiftController.pendingRequest = null;
      }
      if (this.modalOpenTimer) {
        window.clearTimeout(this.modalOpenTimer);
        this.modalOpenTimer = null;
      }
      this.cleanupRequestTokenFromForm();

      this.form = null;
      this.modal = null;
      this.modalDialog = null;
      this.modalForm = null;
      this.hasModalFlow = false;
      this.isEnabled = false;
      this.activeModalSubmitter = null;
      this.modalOpenTimer = null;
    }

    handleOwnFormSubmit () {
      if (!this.form) return;

      const requestToken = this.generateRequestToken();
      let tokenInput = this.form.querySelector(`input[name="${this.requestTokenField}"]`);

      if (!tokenInput) {
        tokenInput = document.createElement('input');
        tokenInput.type = 'hidden';
        tokenInput.name = this.requestTokenField;
        tokenInput.setAttribute('data-free-gift-request-token', '');
        this.form.appendChild(tokenInput);
      }

      tokenInput.value = requestToken;
      FreeGiftController.requestTokenControllers.set(requestToken, this);
      this.scheduleTokenCleanup(requestToken);
    }

    cleanupRequestTokenFromForm () {
      if (!this.form) return;
      const tokenInput = this.form.querySelector(`input[name="${this.requestTokenField}"]`);
      const token = tokenInput?.value ? String(tokenInput.value) : '';
      if (!token) return;

      FreeGiftController.requestTokenControllers.delete(token);
      const cleanupTimer = FreeGiftController.requestTokenCleanupTimers.get(token);
      if (cleanupTimer) {
        window.clearTimeout(cleanupTimer);
        FreeGiftController.requestTokenCleanupTimers.delete(token);
      }
    }

    scheduleTokenCleanup (token) {
      if (!token) return;

      const existingTimer = FreeGiftController.requestTokenCleanupTimers.get(token);
      if (existingTimer) {
        window.clearTimeout(existingTimer);
      }

      const cleanupTimer = window.setTimeout(() => {
        FreeGiftController.requestTokenControllers.delete(token);
        FreeGiftController.requestTokenCleanupTimers.delete(token);
      }, 20000);

      FreeGiftController.requestTokenCleanupTimers.set(token, cleanupTimer);
    }

    static ensureXhrInterceptor () {
      if (FreeGiftController.xhrInterceptorReady) return;

      FreeGiftController.nativeOpen = XMLHttpRequest.prototype.open;
      FreeGiftController.nativeSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url, async, user, password) {
        this.__freeGiftMeta = { method, url };
        return FreeGiftController.nativeOpen.call(this, method, url, async, user, password);
      };

      XMLHttpRequest.prototype.send = function (body) {
        if (typeof body !== 'string' || body === '') {
          return FreeGiftController.nativeSend.call(this, body);
        }

        const requestMatch = FreeGiftController.findControllerForRequest(this, body);
        if (!requestMatch) {
          return FreeGiftController.nativeSend.call(this, body);
        }

        const { controller, token, bodyWithoutToken } = requestMatch;
        if (!controller) {
          if (token) {
            FreeGiftController.requestTokenControllers.delete(token);
            const cleanupTimer = FreeGiftController.requestTokenCleanupTimers.get(token);
            if (cleanupTimer) {
              window.clearTimeout(cleanupTimer);
              FreeGiftController.requestTokenCleanupTimers.delete(token);
            }
          }
          return FreeGiftController.nativeSend.call(this, bodyWithoutToken);
        }

        if (token) {
          FreeGiftController.requestTokenControllers.delete(token);
          const cleanupTimer = FreeGiftController.requestTokenCleanupTimers.get(token);
          if (cleanupTimer) {
            window.clearTimeout(cleanupTimer);
            FreeGiftController.requestTokenCleanupTimers.delete(token);
          }
        }

        controller.interceptRequest(this, bodyWithoutToken);
      };

      FreeGiftController.xhrInterceptorReady = true;
    }

    static findControllerForRequest (xhr, body) {
      const params = new URLSearchParams(body);
      const token = params.get('_fgc_request_token');
      if (token) {
        params.delete('_fgc_request_token');
      }

      const bodyWithoutToken = params.toString();
      if (!token) {
        return null;
      }

      const controller = FreeGiftController.requestTokenControllers.get(token) || null;
      if (!controller) {
        return { controller: null, token, bodyWithoutToken };
      }

      if (!controller.shouldIntercept(xhr, params)) {
        return { controller: null, token, bodyWithoutToken };
      }

      return { controller, token, bodyWithoutToken };
    }

    shouldIntercept (xhr, params) {
      if (!this.isEnabled) return false;
      if (FreeGiftController.pendingRequest) return false;
      if (this.hasItemsPayload(params.toString())) return false;

      const meta = xhr?.__freeGiftMeta ?? {};
      const method = String(meta.method || '').toUpperCase();
      if (method !== 'POST') return false;
      if (!this.isCartAddRequest(meta.url)) return false;

      const shouldOpenGiftFlow = this.isTruthy(params.get('properties[_needle_free_gift]'));

      if (!shouldOpenGiftFlow) return false;
      if (!params.get('id')) return false;

      return true;
    }

    interceptRequest (xhr, originalBody) {
      if (!this.hasModalFlow) {
        const fallbackBody = this.buildFallbackBody(originalBody);
        FreeGiftController.nativeSend.call(xhr, fallbackBody);
        return;
      }

      FreeGiftController.pendingRequest = {
        controller: this,
        xhr,
        originalBody,
        isConfirmed: false,
        modalSubmitter: null,
      };

      this.modalOpenTimer = window.setTimeout(() => {
        this.modalOpenTimer = null;
        if (typeof this.modal?.popupOpen === 'function') {
          this.modal.popupOpen();
        }
        this.stopMainLoadingIndicators();
      }, this.modalOpenDelay);
    }

    handleModalSubmit (event) {
      const pending = FreeGiftController.pendingRequest;
      if (!pending || pending.controller !== this) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      const submitter = event.submitter;
      if (submitter instanceof HTMLButtonElement && submitter.disabled) {
        return;
      }

      this.startModalLoadingIndicators(submitter);

      const modalFormData = new FormData(this.modalForm);
      const body = this.buildCombinedItemsRequestBody(pending.originalBody, modalFormData);

      pending.isConfirmed = true;

      this.resumePendingRequest(body);
    }

    handleModalClose () {
      const pending = FreeGiftController.pendingRequest;
      if (!pending || pending.controller !== this) return;
      if (pending.isConfirmed) return;

      window.requestAnimationFrame(() => {
        if (typeof this.modal?.popupOpen === 'function') {
          this.modal.popupOpen();
        }
      });
    }

    resumePendingRequest (body) {
      const pending = FreeGiftController.pendingRequest;
      if (!pending) return;

      pending.xhr.addEventListener('loadend', () => {
        const isSuccess = pending.xhr.status >= 200 && pending.xhr.status < 300;
        if (isSuccess && typeof this.modal?.popupClose === 'function') {
          this.modal.popupClose();
        }
        this.stopModalLoadingIndicators();
      }, { once: true });

      FreeGiftController.pendingRequest = null;
      FreeGiftController.nativeSend.call(pending.xhr, body);
    }

    buildFallbackBody (originalBody) {
      const giftVariantId = this.resolveFallbackGiftVariantId();
      if (!giftVariantId) {
        return this.buildMainOnlyRequestBody(originalBody);
      }

      return this.buildCombinedItemsRequestBody(originalBody, null, giftVariantId);
    }

    resolveFallbackGiftVariantId () {
      if (this.fallbackGiftVariantId) return this.fallbackGiftVariantId;

      if (this.modalForm) {
        const giftVariantId = this.normalizeId(this.modalForm.querySelector('[name="id"]')?.value);
        if (giftVariantId) return giftVariantId;
      }

      return null;
    }

    buildMainOnlyRequestBody (originalBody) {
      if (typeof originalBody !== 'string' || !originalBody) return originalBody;

      const source = new URLSearchParams(originalBody);
      const output = new URLSearchParams();

      source.forEach((value, key) => {
        if (key === this.requestTokenField) return;
        if (key === 'properties[_needle_free_gift]') return;
        output.append(key, value);
      });

      return output.toString();
    }

    buildCombinedItemsRequestBody (originalBody, modalFormData = null, forcedGiftVariantId = null) {
      if (typeof originalBody !== 'string' || !originalBody) return originalBody;

      const source = new URLSearchParams(originalBody);
      const mainVariantId = String(source.get('id') || '').trim();
      const giftVariantId = forcedGiftVariantId || String(modalFormData?.get('id') || '').trim();

      if (!mainVariantId || !giftVariantId) {
        return this.buildMainOnlyRequestBody(originalBody);
      }

      const mainQuantity = String(source.get('quantity') || '1').trim() || '1';
      const giftQuantity = String(modalFormData?.get('quantity') || '1').trim() || '1';
      const output = new URLSearchParams();

      const mainProperties = this.readPropertiesFromUrlParams(source);
      const giftProperties = modalFormData ? this.readPropertiesFromFormData(modalFormData) : {};
      const passthrough = [];

      source.forEach((value, key) => {
        if (key === 'id' || key === 'quantity') return;
        if (key.startsWith('properties[')) return;
        if (key === this.requestTokenField) return;
        passthrough.push([key, value]);
      });

      giftProperties._free_gift_for = mainVariantId;
      if (Object.prototype.hasOwnProperty.call(giftProperties, '_needle_free_gift')) {
        delete giftProperties._needle_free_gift;
      }

      output.append('items[0][id]', mainVariantId);
      output.append('items[0][quantity]', mainQuantity);
      Object.entries(mainProperties).forEach(([key, value]) => {
        if (key === '_needle_free_gift') return;
        output.append(`items[0][properties][${key}]`, value);
      });

      output.append('items[1][id]', giftVariantId);
      output.append('items[1][quantity]', giftQuantity);
      Object.entries(giftProperties).forEach(([key, value]) => {
        output.append(`items[1][properties][${key}]`, value);
      });

      passthrough.forEach(([key, value]) => {
        output.append(key, value);
      });

      return output.toString();
    }

    readPropertiesFromUrlParams (params) {
      const properties = {};

      params.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)]$/);
        if (!match) return;
        properties[match[1]] = value;
      });

      return properties;
    }

    readPropertiesFromFormData (formData) {
      const properties = {};

      for (const [key, value] of formData.entries()) {
        const match = String(key).match(/^properties\[(.+)]$/);
        if (!match) continue;
        properties[match[1]] = value;
      }

      return properties;
    }

    stopMainLoadingIndicators () {
      const submitButton = this.form?.querySelector('button[name="add"], button[type="submit"], .add-to-cart');
      if (submitButton) {
        submitButton.classList.remove('btn--loading');
        submitButton.removeAttribute('data-loading');
      }

      this.setHeaderLoading(false);
    }

    startModalLoadingIndicators (submitter) {
      const pending = FreeGiftController.pendingRequest;
      if (!pending) return;

      const button = submitter instanceof HTMLElement
        ? submitter
        : this.modalForm?.querySelector('button[name="add"], button[type="submit"], .add-to-cart');

      pending.modalSubmitter = button || null;
      this.activeModalSubmitter = pending.modalSubmitter;

      if (pending.modalSubmitter) {
        pending.modalSubmitter.classList.add('btn--loading');
        pending.modalSubmitter.setAttribute('data-loading', '');
      }

      this.setHeaderLoading(true);
    }

    stopModalLoadingIndicators () {
      const button = this.activeModalSubmitter;

      if (button) {
        button.classList.remove('btn--loading');
        button.removeAttribute('data-loading');
      }
      this.activeModalSubmitter = null;

      this.setHeaderLoading(false);
    }

    setHeaderLoading (state) {
      const stickyHeader = document.querySelector('sticky-header');
      if (!stickyHeader) return;

      if (state) {
        stickyHeader.classList.add('loading-bar');
      } else {
        stickyHeader.classList.remove('loading-bar');
      }
    }

    normalizeId (value) {
      if (value == null) return null;
      const normalized = String(value).trim();
      return normalized !== '' ? normalized : null;
    }

    normalizeDelay (value, fallback) {
      const parsed = Number.parseInt(String(value ?? ''), 10);
      if (Number.isFinite(parsed) && parsed >= 0) return parsed;
      return fallback;
    }

    generateRequestToken () {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }

      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    isTruthy (value) {
      if (value == null) return false;
      const normalized = String(value).trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
    }

    hasItemsPayload (body) {
      return body.includes('items[') || body.includes('items%5B');
    }

    isCartAddRequest (url) {
      if (!url) return false;

      const requestPath = this.normalizeRequestUrl(url);
      const configuredPath = this.normalizeRequestUrl(this.cartAddUrl);

      if (configuredPath && requestPath.includes(configuredPath)) {
        return true;
      }

      return /\/cart\/add(?:\.js)?(?:\?|$)/.test(requestPath);
    }

    normalizeRequestUrl (url) {
      if (!url) return '';

      try {
        const parsed = new URL(url, window.location.origin);
        return `${parsed.pathname}${parsed.search}`;
      } catch (error) {
        return String(url);
      }
    }
  }

  FreeGiftController.controllers = new Set();
  FreeGiftController.pendingRequest = null;
  FreeGiftController.xhrInterceptorReady = false;
  FreeGiftController.nativeOpen = null;
  FreeGiftController.nativeSend = null;
  FreeGiftController.requestTokenControllers = new Map();
  FreeGiftController.requestTokenCleanupTimers = new Map();

  customElements.define('free-gift-controller', FreeGiftController);
}
